import { TextInput, AutoCompleteInput, CustomSelect, PhoneInput } from '@/shared/ui/components';
import { isWebViewCreateStudy, phoneFormat } from '@/shared/lib';
import { forwardRef } from 'react';
import { t } from 'ttag';
import { GOOGLE_API_KEY } from '@/shared/config';
import { useNewStudyStep2 } from './useNewStudyStep2';
import { handleKeyDown } from './handler';
import NewStudyStepSkeleton from '../newStudyStepSkeleton/newStudyStepSkeleton';
import type { NewStudyStep2Props } from './newStudyStep2.types';
import './newStudyStep2.scss';

/**
 * Second step of the new study wizard collecting patient and secondary contact details. Returns NewStudyStepSkeleton until props.isLoadedData, otherwise renders country, autocompleted address and city, a state select or free-text field depending on shouldFormatPhoneNumber, zip, phone numbers run through phoneFormat, an email whose optional label follows isEmailRequired, and the optional secondary contact fields.
 *
 * @param props - Step props including the country list and loading flags.
 * @param ref - Ref exposing the step imperative handle.
 * @returns The contact step element, or the step skeleton while loading.
 */
const NewStudyStep2 = forwardRef((props: NewStudyStep2Props, ref) => {
  const {
    state,
    isEmailRequired,
    shouldFormatPhoneNumber,
    onChange,
    onChangeSelection,
    clearAutoComplete,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
  } = useNewStudyStep2(props, ref);

  const isWebView = isWebViewCreateStudy();
  const idArr = [
    'country',
    'address',
    'city',
    'state',
    'zip',
    'phone1',
    'email',
    'phone2',
    'secondaryName',
    'secondaryRelationship',
    'secondaryPhone',
    'secondaryEmail',
  ];

  if (!props.isLoadedData) {
    return <NewStudyStepSkeleton />;
  }

  return (
    <div className="step-content step-2">
      <div className="centered-content">
        <div>
          <div className="centered-content__header">{t`Patient contact`}</div>
          <div className="centered-content__body">
            <CustomSelect
              id="country"
              title={t`Country`}
              aria-label={t`Country`}
              showSearch
              allowClear={false}
              isError={!!state.errorObject.country}
              errorMessage={state.errorObject.country}
              name="country"
              size="middle"
              options={props.countries}
              value={state.country}
              onChange={onChangeSelection}
            />
            <AutoCompleteInput
              googleApiKey={GOOGLE_API_KEY}
              id="address"
              allowClear
              size="small"
              isSearchCity={false}
              title={t`Address`}
              placeholder={t`Enter address`}
              isError={!!state.errorObject.address}
              country={state.newStudyStep2Data.country}
              errorMessage={state.errorObject.address}
              onChangeInput={onChangeInputAutoComplete}
              onChangeAddress={onChangeAutoComplete}
              eventClear={clearAutoComplete}
              disabled={props.isLoadingDivisions}
              value={state.newStudyStep2Data.address}
            />
            <div className="inline-input">
              <AutoCompleteInput
                googleApiKey={GOOGLE_API_KEY}
                id="city"
                className="city-input"
                size="small"
                isSearchCity
                title={t`City`}
                placeholder={t`Enter city`}
                country={state.newStudyStep2Data.country}
                value={state.newStudyStep2Data.city}
                onChangeInput={onChangeInputAutoComplete}
                onChangeAddress={onChangeAutoComplete}
                eventClear={clearAutoComplete}
                disabled={props.isLoadingDivisions}
                isError={!!state.errorObject.city}
                errorMessage={state.errorObject.city}
              />
              {shouldFormatPhoneNumber ? (
                <CustomSelect
                  id="state"
                  title={t`State`}
                  aria-label={t`State`}
                  className="state-input"
                  showSearch
                  allowClear={false}
                  isError={!!state.errorObject.state}
                  errorMessage={state.errorObject.state}
                  name="state"
                  size="middle"
                  placeholder={props.isLoadingDivisions ? t`Loading...` : t`Select a state`}
                  options={state.divisions}
                  value={state.state}
                  onChange={onChangeSelection}
                />
              ) : (
                <TextInput
                  id="state"
                  onKeyDown={(e) => handleKeyDown(e, idArr)}
                  className="state-input"
                  name="state"
                  label={t`State`}
                  placeholder={t`Enter state`}
                  value={state.newStudyStep2Data.state}
                  onChange={onChange}
                  isError={!!state.errorObject.state}
                  errorMessage={state.errorObject.state}
                />
              )}
            </div>
            <TextInput
              id="zip"
              name="zip"
              onKeyDown={(e) => handleKeyDown(e, idArr)}
              label={t`Zip code`}
              placeholder={t`Enter zip code`}
              value={state.newStudyStep2Data.zip}
              onChange={onChange}
              disabled={props.isLoadingDivisions}
              isError={!!state.errorObject.zip}
              errorMessage={state.errorObject.zip}
            />
            <PhoneInput
              id="patient-contact-primary-phone-number"
              title={t`Primary phone number`}
              name="phone1"
              countryValue={state.newStudyStep2Data.country}
              value={phoneFormat(state.newStudyStep2Data.phone1, state.newStudyStep2Data.country)}
              placeholder={shouldFormatPhoneNumber ? '800-555-1234' : t`Enter primary phone number`}
              onChange={onChange}
              size="small"
              isError={!!state.errorObject.phone1}
              errorMessage={state.errorObject.phone1}
              maxLength={15}
              countryOptions={props.countries}
            />
            <PhoneInput
              id="patient-contact-secondary-phone-number"
              title={
                <div>
                  {t`Secondary phone number`} <span className="optional-label">(optional)</span>
                </div>
              }
              name="phone2"
              countryValue={state.newStudyStep2Data.country}
              value={phoneFormat(state.newStudyStep2Data.phone2, state.newStudyStep2Data.country)}
              placeholder={
                shouldFormatPhoneNumber ? '800-555-1234' : t`Enter secondary phone number`
              }
              onChange={onChange}
              size="small"
              isError={!!state.errorObject.phone2}
              errorMessage={state.errorObject.phone2}
              maxLength={15}
              countryOptions={props.countries}
            />
            <TextInput
              id="patient-contact-email"
              name="email"
              onKeyDown={(e) => handleKeyDown(e, idArr)}
              label={
                <div>
                  {t`Email`}{' '}
                  {isEmailRequired ? null : <span className="optional-label">(optional)</span>}
                </div>
              }
              placeholder="johnsmith@mail.com"
              value={state.newStudyStep2Data.email}
              onChange={onChange}
              isError={!!state.errorObject.email}
              errorMessage={state.errorObject.email}
              hasErrorIcon
            />
          </div>
        </div>
        <div className={isWebView ? 'sub-header' : 'mt-40'}>
          <div className="centered-content__header">{t`Secondary contact`}</div>
          <div className="centered-content__body">
            <TextInput
              id="secondary-contact-name"
              name="secondaryName"
              onKeyDown={(e) => handleKeyDown(e, idArr)}
              label={
                <div>
                  {t`Name`} <span className="optional-label">(optional)</span>
                </div>
              }
              placeholder={t`Enter name`}
              value={state.newStudyStep2Data.secondaryName}
              onChange={onChange}
            />
            <TextInput
              id="relationship"
              name="secondaryRelationship"
              onKeyDown={(e) => handleKeyDown(e, idArr)}
              label={
                <div>
                  {t`Relationship`} <span className="optional-label">(optional)</span>
                </div>
              }
              placeholder={t`Enter relationship`}
              value={state.newStudyStep2Data.secondaryRelationship}
              onChange={onChange}
            />
            <PhoneInput
              id="secondary-contact-phone-number"
              title={
                <div>
                  {t`Phone number`} <span className="optional-label">(optional)</span>
                </div>
              }
              name="secondaryPhone"
              countryValue={state.newStudyStep2Data.country}
              value={phoneFormat(
                state.newStudyStep2Data.secondaryPhone,
                state.newStudyStep2Data.country,
              )}
              placeholder={shouldFormatPhoneNumber ? '800-555-1234' : t`Enter phone number`}
              onChange={onChange}
              size="small"
              isError={!!state.errorObject.secondaryPhone}
              errorMessage={state.errorObject.secondaryPhone}
              maxLength={15}
              countryOptions={props.countries}
            />
            <TextInput
              id="secondary-contact-email"
              name="secondaryEmail"
              onKeyDown={(e) => handleKeyDown(e, idArr)}
              label={
                <div>
                  {t`Email`} <span className="optional-label">(optional)</span>
                </div>
              }
              placeholder="johnsmith@mail.com"
              value={state.newStudyStep2Data.secondaryEmail}
              onChange={onChange}
              isError={!!state.errorObject.secondaryEmail}
              errorMessage={state.errorObject.secondaryEmail}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default NewStudyStep2;
